import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import aiAssistantService from "../../services/aiAssistantService";
import { canSendTurn, errorDetails, handleTurnFailure, initialAssistantState, isCredentialSupersededError, newTurnId } from "./aiAssistantState";
import robotNatural from "./assets/BBOT-natural.svg";
import robotBlushing from "./assets/BBOT Blushing.svg";
import robotSelections from "./assets/BBOT-selections.svg";
import pathBackdrop from "./assets/Path 443.svg";
import sendIcon from "./assets/Send.svg";
import promptMarkIcon from "./assets/Prompt-mark.svg";
import backIcon from "./assets/Back.svg";
import moreIcon from "./assets/More.svg";
import questionIcon from "./assets/Question.svg";
import documentIcon from "./assets/Document.svg";
import chartIcon from "./assets/Chart.svg";
import menuTrashIcon from "./assets/Menu-trash.svg";
import { AnswerReviewButtons, AnswerReviewDialog } from "./AiAssistantAnswerReview";
import "./AiAssistantPanel.css";

const ANSWER_KIND_TEXT = {
    OFFICIAL: "התשובה נוצרה על ידי AI ועלולה לכלול טעויות — מומלץ לבדוק מול נוסח החוק",
    BTAX_MATERIALS: "מידע עזר של B-TAX — התשובה נוצרה על ידי AI ועלולה לכלול טעויות"
};
const LEGACY_ASSISTANT_LINES = new Set([
  "התשובה מבוססת על מקורות ממשלתיים רשמיים",
  "התשובה מבוססת על מקורות רשמיים של Btax",
  "הסבר כללי, אינו מהווה חוות דעת משפטית",
  "מידע עזר של B-TAX — מומלץ לאמת מול הנוסח הרשמי של החוק"
]);

const ASSISTANT_HINTS = [
  { text: "איך אתה יכול לעזור?", icon: questionIcon, response: ["איזה כיף ששאלת!\n\nאני כאן כדי לעזור לך בכל מה שקשור לעסקה שלך: אפשר לשאול אותי שאלות מקצועיות, לקבל הסבר על שדות ושלבים בטופס העסקה, ולמלא בטופס פרטים מתוך ההודעות שלך.", "ואם אחשוב שבמקרה שלך עדיף שאיש מקצוע מטעמנו יבדוק את הדברים לעומק, אדע גם להפנות אותך לגורם המתאים."] },
  { text: "אשמח ליצור קשר", icon: documentIcon, response: ["בשמחה!\n\nאפשר להתקשר אלינו, ואחד מהנציגים שלנו ישמח לעזור.", "תמיכה טכנית: 03-5615788\nתמיכה מקצועית: 050-5927888"] },
  { text: "איך ממלאים מכירה של חלק מהזכויות?", icon: chartIcon }
];
const DRAFT_CHANGE_NOTICE = "עוזר ה-AI עדכן את טופס העסקה. יש לבדוק את השינויים — העוזר עלול לטעות.";
const STALE_FORM_NOTICE = "הטופס השתנה בזמן שהעוזר ענה, ולכן השינויים מהתשובה הזו לא הוחלו בטופס. אפשר לבקש שוב.";
const GREETING_NAME_PLACEHOLDERS = new Set(["undefined", "{fullName}"]);
const INTRO_HOLD_MS = 1600;
const INTRO_HINTS_DELAY_MS = 600;
const BLUSH_MS = 5000;
const ANALYSIS_READY_NOTICE = "יש בטופס מספיק נתונים לניתוח. אפשר ללחוץ על «נתח עסקה».";

export function answerKindText(answerKind) {
  return ANSWER_KIND_TEXT[answerKind] || null;
}

export function sanitizeAssistantText(text) {
  const isSignatureLine = (line) => {
    const trimmedLine = line.trim();
    const unwrappedLine = trimmedLine.replace(/\.$/, "").replace(/^\*\*(.*)\*\*$|^\*(.*)\*$|^_(.*)_$/, "$1$2$3").replace(/\.$/, "").trim();
    return [...Object.values(ANSWER_KIND_TEXT), ...LEGACY_ASSISTANT_LINES].some((marker) => unwrappedLine === marker);
  };
  return (text || "").split("\n").filter((line) => !isSignatureLine(line)).join("\n").trim();
}

function message(role, text, answer, metadata = {}) {
  return { role, text: role === "assistant" ? sanitizeAssistantText(text) : text, answerKind: answer?.kind || null, ...metadata };
}

// A history row cannot know what this page's form holds, so it never says the assistant changed the form.
// Only an answered assistant row can be rated; reviewTurnId names it for the feedback route.
function historyMessage(turn) {
  const text = turn.text || (turn.role === "assistant" ? "לא התקבלה תשובה לשאלה זו." : "");
  const reviewable = turn.role === "assistant" && turn.text && turn.turnId;
  return message(turn.role, text, turn.role === "assistant" ? { kind: turn.answerKind } : null, { historySeq: turn.seq, ...(reviewable ? { reviewTurnId: turn.turnId, review: turn.review || null } : {}) });
}

function isPermanentTurnRejection(error) {
  if (isCredentialSupersededError(error)) return false;
  const { status, code } = errorDetails(error);
  // Timeouts, early requests and throttling can clear; SESSION_ENDED reaches this check after its automatic resend.
  return typeof status === "number" && status >= 400 && status < 500 && ![401, 403, 408, 425, 429].includes(status) && !(status === 404 && code === "SESSION_ENDED");
}

function renderMixedDirectionText(text) {
  const parts = String(text || "").split(/(\b\d{2,3}-\d{7}\b|₪[\d,]+|\b\d{1,2}\/\d{1,2}\/\d{4}\b|correlationRef=[A-Za-z0-9_-]+|\b[0-9a-f]{8,}\b)/gi);
  return parts.map((part, index) => /^(?:\d{2,3}-\d{7}|₪[\d,]+|\d{1,2}\/\d{1,2}\/\d{4}|correlationRef=[A-Za-z0-9_-]+|[0-9a-f]{8,})$/i.test(part) ? <span key={index} dir="ltr" className="ai-assistant-ltr">{part}</span> : part);
}

export function renderAssistantText(text) {
  const source = String(text || "");
  const parts = [];
  const boldPattern = /\*\*([\s\S]+?)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = boldPattern.exec(source))) {
    if (match.index > lastIndex) parts.push(<React.Fragment key={`text-${lastIndex}`}>{renderMixedDirectionText(source.slice(lastIndex, match.index))}</React.Fragment>);
    parts.push(<strong key={`bold-${match.index}`}>{renderMixedDirectionText(match[1])}</strong>);
    lastIndex = boldPattern.lastIndex;
  }
  if (!parts.length) return renderMixedDirectionText(source);
  if (lastIndex < source.length) parts.push(<React.Fragment key={`text-${lastIndex}`}>{renderMixedDirectionText(source.slice(lastIndex))}</React.Fragment>);
  return parts;
}

export default function AiAssistantPanel({ open, onOpenChange, dealId, calc, manage, getDealState, formProgress, manualEditRevision, onFacts, onFormChange, fullName, answerReviewEnabled = false }) {
  const [state, setState] = useState(initialAssistantState);
  const [input, setInput] = useState("");
  const [introHasPlayed, setIntroHasPlayed] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introDocked, setIntroDocked] = useState(false);
  const [introTraveling, setIntroTraveling] = useState(false);
  const [introFading, setIntroFading] = useState(false);
  const [introHintsVisible, setIntroHintsVisible] = useState(false);
  const [isBlushing, setIsBlushing] = useState(false);
  const [launcherReturning, setLauncherReturning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reviewDialogTurnId, setReviewDialogTurnId] = useState(null);
  const launcherRef = useRef(null);
  const launcherReturnTimeoutRef = useRef(null);
  const introDockTimeoutRef = useRef(null);
  const introHintsTimeoutRef = useRef(null);
  const blushTimeoutRef = useRef(null);
  const menuButtonRef = useRef(null);
  const inputRef = useRef(null);
  const inputMeasureRef = useRef(null);
  const composerRef = useRef(null);
  const avatarRef = useRef(null);
  const introMessageRef = useRef(null);
  const introFromRectRef = useRef(null);
  const bodyRef = useRef(null);
  const lowerStageRef = useRef(null);
  const threadRef = useRef(null);
  const nodeRef = useRef(null);
  const replyNodeRef = useRef(null);
  const pendingReplyMessageRef = useRef(null);
  const openRef = useRef(open);
  const requestGenerationRef = useRef(0);
  const messagesRef = useRef(state.messages);
  const reviewSavesRef = useRef(new Map());
  const submitLockRef = useRef(false);
  const closePanelRef = useRef(null);
  const openingRef = useRef(false);
  const attemptedDealRef = useRef(null);
  const openSessionRef = useRef(null);
  const previousDealRef = useRef(dealId);
  const formProgressRef = useRef(formProgress);
  const previousAnalysisReadyRef = useRef(Boolean(formProgress?.analysisReady));
  const manualEditRevisionRef = useRef(manualEditRevision);
  const previousManualEditRevisionRef = useRef(manualEditRevision);
  const readinessNoticeCandidateRef = useRef(null);
  const formSnapshotRef = useRef(null);
  const lastTurnRequestRef = useRef(null);
  const historyLoadingRef = useRef(false);
  const historyRequestGenerationRef = useRef(0);
  const historyHasMoreRef = useRef(false);
  const scrollRestoreRef = useRef(null);
  const scrollToBottomRef = useRef(false);
  const followLatestMessageRef = useRef(true);
  const closedScrollTopRef = useRef(null);
  const closedMessageCountRef = useRef(null);
  const reopenScrollTopRef = useRef(null);
  const lastMessageText = state.messages.length ? state.messages[state.messages.length - 1].text : null;
  const dailyResetAt = state.daily.resetAt ? Date.parse(state.daily.resetAt) : null;
  const dailyLimitReached = state.daily.remaining === 0 && (!Number.isFinite(dailyResetAt) || dailyResetAt > Date.now());
  const limitReached = dailyLimitReached && Number.isFinite(dailyResetAt) && dailyResetAt > Date.now();
  const introIsDocked = introDocked || introHasPlayed;
  const suggestionsVisible = showIntro && (introHintsVisible || introHasPlayed) && !state.isOpening && !state.error && !state.unavailable && !state.credentialSuperseded && state.messages.length === 0 && !input.trim();
  const robotPose = suggestionsVisible ? "selections" : isBlushing && !state.isSending ? "blushing" : "natural";
  messagesRef.current = state.messages;
  openRef.current = open;
  formProgressRef.current = formProgress;
  manualEditRevisionRef.current = manualEditRevision;

  useEffect(() => {
    if (!Number.isFinite(dailyResetAt)) return undefined;
    if (dailyResetAt <= Date.now()) {
      setState((current) => {
        const currentResetAt = current.daily.resetAt ? Date.parse(current.daily.resetAt) : null;
        return Number.isFinite(currentResetAt) && currentResetAt <= Date.now()
          ? { ...current, daily: { remaining: null, limit: null, resetAt: null } }
          : current;
      });
      return undefined;
    }
    const timeout = window.setTimeout(() => setState((current) => ({ ...current, daily: { remaining: null, limit: null, resetAt: null } })), dailyResetAt - Date.now());
    return () => window.clearTimeout(timeout);
  }, [dailyResetAt]);

  const appendAnalysisReadyNotice = useCallback(() => {
    const candidate = readinessNoticeCandidateRef.current;
    const progress = formProgressRef.current;
    if (!candidate || candidate.dealId !== dealId || candidate.manualEditRevision !== manualEditRevisionRef.current || !progress?.analysisReady || progress.analysisDone || candidate.replyText.includes("נתח עסקה")) return;
    readinessNoticeCandidateRef.current = null;
    const notice = message("assistant", ANALYSIS_READY_NOTICE);
    setState((current) => ({ ...current, messages: [...current.messages, notice] }));
  }, [dealId]);

  useEffect(() => {
    const isAnalysisReady = Boolean(formProgress?.analysisReady);
    const wasAnalysisReady = previousAnalysisReadyRef.current;
    previousAnalysisReadyRef.current = isAnalysisReady;
    if (!wasAnalysisReady && isAnalysisReady) appendAnalysisReadyNotice();
  }, [formProgress?.analysisReady, formProgress?.analysisDone, appendAnalysisReadyNotice]);

  useEffect(() => {
    if (previousManualEditRevisionRef.current !== manualEditRevision) readinessNoticeCandidateRef.current = null;
    previousManualEditRevisionRef.current = manualEditRevision;
  }, [manualEditRevision]);

  // A conversation snapshot (session open, recovery after a rejection) restores the chat and the position only: the form
  // changes only through the reply of the question that produced the change (completeTurn).
  const adoptConversationSnapshot = ({ session, history, messages, daily, removeMessage = null, requestGeneration = requestGenerationRef.current }) => {
    if (requestGeneration !== requestGenerationRef.current) return false;
    const turns = Array.isArray(history?.turns) ? history.turns.slice().reverse() : [];
    const adoptedMessages = messages || turns.map(historyMessage);
    historyHasMoreRef.current = Boolean(history?.hasMore);
    setShowIntro(adoptedMessages.length === 0);
    scrollToBottomRef.current = true;
    setState((current) => ({
      ...current,
      session,
      messages: removeMessage ? adoptedMessages.filter((item) => item !== removeMessage) : adoptedMessages,
      isOpening: false,
      isLoadingHistory: false,
      historyHasMore: Boolean(history?.hasMore),
      historyBeforeSeq: history?.beforeSeq ?? null,
      unavailable: false,
      credentialSuperseded: false,
      error: null,
      ...(daily ? { daily } : {})
    }));
    return true;
  };

  // The session response is one server snapshot: its position and its history page are read after any running turn finished.
  const fetchAndAdoptConversationState = async (newConversation = false, adoption = {}, requestGeneration = requestGenerationRef.current) => {
    const { history, ...session } = await aiAssistantService.openSession(dealId, newConversation);
    const adopted = adoptConversationSnapshot({
      session,
      history: history || { turns: [], hasMore: false, beforeSeq: null },
      daily: adoption.daily,
      messages: newConversation ? [] : undefined,
      removeMessage: adoption.removeMessage,
      requestGeneration
    });
    return adopted ? session : null;
  };

  const setConversationRecoveryError = (error, adoption = {}) => {
    const requestGeneration = adoption.requestGeneration ?? requestGenerationRef.current;
    if (requestGeneration !== requestGenerationRef.current) return;
    setState((current) => ({
      ...current,
      isOpening: false,
      ...(adoption.daily ? { daily: adoption.daily } : {}),
      messages: adoption.removeMessage ? current.messages.filter((item) => item !== adoption.removeMessage) : current.messages,
      unavailable: !isCredentialSupersededError(error),
      credentialSuperseded: isCredentialSupersededError(error),
      error: errorDetails(error)
    }));
  };

  const recoverConversationAfterTurnError = async ({ requestGeneration, adoption = {}, onRecovered }) => {
    setState((current) => ({ ...current, isOpening: true, unavailable: false, credentialSuperseded: false, error: null }));
    try {
      const recovered = await fetchAndAdoptConversationState(false, adoption, requestGeneration);
      if (!recovered) return;
      if (onRecovered) onRecovered();
    } catch (recoveryError) {
      setConversationRecoveryError(recoveryError, { ...adoption, requestGeneration });
    }
  };

  const openSession = (force = false, newConversation = false) => {
    if (!open || !dealId || (!force && state.session) || openingRef.current || (!force && attemptedDealRef.current === dealId)) return;
    attemptedDealRef.current = dealId;
    openingRef.current = true;
    const requestGeneration = requestGenerationRef.current;
    setState((current) => ({ ...current, isOpening: true, unavailable: false, credentialSuperseded: false, error: null }));
    fetchAndAdoptConversationState(newConversation, {}, requestGeneration)
      .catch((error) => setConversationRecoveryError(error, { requestGeneration }))
      .finally(() => { if (requestGeneration === requestGenerationRef.current) openingRef.current = false; });
  };
  openSessionRef.current = openSession;
  useEffect(() => () => {
    window.clearTimeout(launcherReturnTimeoutRef.current);
    window.clearTimeout(introDockTimeoutRef.current);
    window.clearTimeout(introHintsTimeoutRef.current);
    window.clearTimeout(blushTimeoutRef.current);
  }, []);
  const dockIntro = (travel = true) => {
    const intro = introMessageRef.current;
    introFromRectRef.current = travel && intro ? intro.getBoundingClientRect() : null;
    setIntroDocked(true);
    setIntroTraveling(Boolean(introFromRectRef.current));
    setIntroFading(!travel);
  };

  useEffect(() => {
    if (!open || !showIntro || introHasPlayed || state.isOpening || state.messages.length) return undefined;
    introDockTimeoutRef.current = window.setTimeout(() => {
      dockIntro();
      introHintsTimeoutRef.current = window.setTimeout(() => {
        setIntroHintsVisible(true);
        setIntroHasPlayed(true);
      }, INTRO_HINTS_DELAY_MS);
    }, INTRO_HOLD_MS);
    return () => {
      window.clearTimeout(introDockTimeoutRef.current);
      window.clearTimeout(introHintsTimeoutRef.current);
    };
  }, [open, showIntro, introHasPlayed, state.isOpening, state.messages.length]);

  const clearBlush = () => {
    window.clearTimeout(blushTimeoutRef.current);
    blushTimeoutRef.current = null;
    setIsBlushing(false);
  };

  const startBlush = () => {
    if (!openRef.current) return;
    window.clearTimeout(blushTimeoutRef.current);
    setIsBlushing(true);
    blushTimeoutRef.current = window.setTimeout(() => {
      blushTimeoutRef.current = null;
      setIsBlushing(false);
    }, BLUSH_MS);
  };

  const resizeInput = () => {
    const textarea = inputRef.current;
    const measure = inputMeasureRef.current;
    if (!textarea || !measure) return;
    measure.style.width = `${textarea.offsetWidth}px`;
    const styles = window.getComputedStyle(textarea);
    const minHeight = parseFloat(styles.minHeight) || 0;
    const maxHeight = parseFloat(styles.maxHeight) || Infinity;
    const contentHeight = measure.scrollHeight;
    textarea.style.height = `${Math.max(minHeight, Math.min(contentHeight, maxHeight))}px`;
    textarea.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  };
  useEffect(() => {
    if (!open) return;
    if (closedMessageCountRef.current !== null) {
      if (messagesRef.current.length > closedMessageCountRef.current || scrollToBottomRef.current) {
        scrollToBottomRef.current = true;
      } else {
        reopenScrollTopRef.current = closedScrollTopRef.current;
      }
      closedScrollTopRef.current = null;
      closedMessageCountRef.current = null;
    }
    openSessionRef.current();
    window.requestAnimationFrame(() => nodeRef.current?.focus());
  }, [open]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    if (scrollRestoreRef.current) {
      const anchor = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
      thread.scrollTop = anchor.top + thread.scrollHeight - anchor.height;
      followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24;
    } else if (reopenScrollTopRef.current !== null) {
      thread.scrollTop = reopenScrollTopRef.current;
      reopenScrollTopRef.current = null;
      followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24;
    } else if (scrollToBottomRef.current || state.isSending) {
      scrollToBottomRef.current = false;
      thread.scrollTop = thread.scrollHeight;
      followLatestMessageRef.current = true;
    }
  }, [open, state.isOpening, state.isSending, state.messages.length, lastMessageText]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!open || !thread || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (followLatestMessageRef.current) thread.scrollTop = thread.scrollHeight;
    });
    observer.observe(thread);
    thread.querySelectorAll(".ai-assistant-bubble, .ai-assistant-greeting").forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [open, state.messages.length]);

  useLayoutEffect(() => {
    const thread = threadRef.current;
    const composer = composerRef.current;
    const pendingReply = pendingReplyMessageRef.current;
    const reply = replyNodeRef.current;
    if (!pendingReply || !messagesRef.current.includes(pendingReply)) return;
    if (!reply || !composer) return;
    thread.scrollTop = thread.scrollHeight;
    const threadRect = thread.getBoundingClientRect();
    const replyRect = reply.getBoundingClientRect();
    const composerRect = composer.getBoundingClientRect();
    const avatarRect = avatarRef.current?.getBoundingClientRect();
    const originTop = avatarRect ? avatarRect.top : composerRect.top;
    const requestedTravel = Math.max(0, originTop - replyRect.bottom);
    const availableTravel = Math.max(0, threadRect.height - Math.min(replyRect.height, threadRect.height));
    const distance = Math.min(requestedTravel, availableTravel);
    reply.style.setProperty("--assistant-arrival-y", `${distance}px`);
    pendingReplyMessageRef.current = null;
  }, [state.messages.length]);

  useLayoutEffect(() => {
    const from = introFromRectRef.current;
    const intro = introMessageRef.current;
    if (!introTraveling || !from || !intro) return;
    introFromRectRef.current = null;
    const to = intro.getBoundingClientRect();
    intro.style.setProperty("--assistant-intro-y", `${Math.round(from.top - to.top)}px`);
  }, [introTraveling, introDocked]);

  useLayoutEffect(() => {
    resizeInput();
  }, [input, open]);

  useLayoutEffect(() => {
    const panel = nodeRef.current;
    const layout = panel?.closest(".deal-assistant-layout");
    if (!open || !panel || !layout) return undefined;
    const syncAvailableHeight = () => {
      const naturalTop = layout.getBoundingClientRect().top + window.scrollY;
      panel.style.setProperty("--assistant-available-height", `${Math.max(0, document.documentElement.clientHeight - naturalTop)}px`);
    };
    syncAvailableHeight();
    window.addEventListener("resize", syncAvailableHeight);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncAvailableHeight);
    observer?.observe(document.documentElement);
    return () => {
      window.removeEventListener("resize", syncAvailableHeight);
      observer?.disconnect();
    };
  }, [open]);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    const stage = lowerStageRef.current;
    if (!open || !body || !stage) return undefined;
    const syncStageHeight = () => body.style.setProperty("--assistant-lower-stage-height", `${stage.offsetHeight}px`);
    syncStageHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(syncStageHeight);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", resizeInput);
    return () => window.removeEventListener("resize", resizeInput);
  }, [open]);

  useEffect(() => {
    if (previousDealRef.current === dealId) return;
    previousDealRef.current = dealId;
    readinessNoticeCandidateRef.current = null;
    requestGenerationRef.current += 1;
    cancelHistoryRequest();
    lastTurnRequestRef.current = null;
    attemptedDealRef.current = null;
    openingRef.current = false;
    submitLockRef.current = false;
    setInput("");
    setState(initialAssistantState);
    setShowIntro(false);
    setIntroHasPlayed(false);
    setIntroDocked(false);
    setIntroTraveling(false);
    setIntroFading(false);
    setIntroHintsVisible(false);
    clearBlush();
    if (openRef.current) window.requestAnimationFrame(() => {
      openSessionRef.current(true);
      nodeRef.current?.focus();
    });
  }, [dealId]);

  useEffect(() => {
    const snapshot = JSON.stringify({ calc, manage });
    if (formSnapshotRef.current !== null && formSnapshotRef.current !== snapshot && onFormChange) onFormChange();
    formSnapshotRef.current = snapshot;
  }, [calc, manage, onFormChange]);

  const startNewConversation = () => {
    if (state.isSending || state.isOpening) return;
    historyRequestGenerationRef.current += 1;
    requestGenerationRef.current += 1;
    historyLoadingRef.current = false;
    historyHasMoreRef.current = false;
    lastTurnRequestRef.current = null;
    setInput("");
    setMenuOpen(false);
    setShowIntro(false);
    setIntroHasPlayed(false);
    setIntroDocked(false);
    setIntroTraveling(false);
    setIntroFading(false);
    setIntroHintsVisible(false);
    clearBlush();
    setState({ ...initialAssistantState, isOpening: true });
    openSession(true, true);
  };

  const loadPreviousHistory = () => {
    if (!historyHasMoreRef.current || historyLoadingRef.current || state.isOpening || !state.historyBeforeSeq) return;
    const thread = threadRef.current;
    if (!thread) return;
    const requestGeneration = historyRequestGenerationRef.current;
    const anchor = { top: thread.scrollTop, height: thread.scrollHeight };
    historyLoadingRef.current = true;
    setState((current) => ({ ...current, isLoadingHistory: true }));
    aiAssistantService.getHistory(dealId, state.historyBeforeSeq)
      .then((history) => {
        if (requestGeneration !== historyRequestGenerationRef.current) return;
        const olderMessages = Array.isArray(history.turns) ? history.turns.slice().reverse().map(historyMessage) : [];
        historyHasMoreRef.current = Boolean(history.hasMore);
        scrollRestoreRef.current = anchor;
        setState((current) => {
          const knownSeqs = new Set(current.messages.map((item) => item.historySeq).filter((seq) => Number.isSafeInteger(seq)));
          const uniqueOlderMessages = olderMessages.filter((item) => {
            if (knownSeqs.has(item.historySeq)) return false;
            knownSeqs.add(item.historySeq);
            return true;
          });
          return { ...current, messages: [...uniqueOlderMessages, ...current.messages], historyHasMore: Boolean(history.hasMore), historyBeforeSeq: history.beforeSeq, isLoadingHistory: false };
        });
      })
      .catch((error) => {
        if (requestGeneration === historyRequestGenerationRef.current) setState((current) => ({ ...current, isLoadingHistory: false, error: errorDetails(error) }));
      })
      .finally(() => {
        if (requestGeneration === historyRequestGenerationRef.current) historyLoadingRef.current = false;
      });
  };

  const cancelHistoryRequest = () => {
    historyRequestGenerationRef.current += 1;
    historyLoadingRef.current = false;
    scrollRestoreRef.current = null;
  };

  // The one place assistant data reaches the form: the reply of this question, against the form it was asked from.
  const completeTurn = async (result, session = null, requestGeneration = requestGenerationRef.current, turnContext = {}, reviewTurnId = null) => {
    if (requestGeneration !== requestGenerationRef.current) return;
    const expectedConversationId = session ? session.conversationId : state.session?.conversationId;
    if (result.conversationRef && result.conversationRef !== expectedConversationId) {
      const error = new Error("Assistant response belongs to another conversation");
      error.code = "CONVERSATION_MISMATCH";
      error.correlationRef = result.correlationRef;
      throw error;
    }
    if (result.correlationRef) console.info("AI assistant request completed", result.correlationRef);
    const wasAnalysisReady = Boolean(formProgressRef.current?.analysisReady);
    const manualEditRevisionAtReply = manualEditRevisionRef.current;
    const daily = { remaining: typeof result.dailyRemaining === "number" ? result.dailyRemaining : null, limit: typeof result.dailyLimit === "number" ? result.dailyLimit : null, resetAt: typeof result.dailyResetAt === "string" ? result.dailyResetAt : null };
    const facts = Array.isArray(result.facts) ? result.facts : [];
    const projection = facts.length && onFacts ? await onFacts(facts, turnContext) : null;
    if (requestGeneration !== requestGenerationRef.current) return;
    const changedDraft = Boolean(projection?.events?.length);
    const reply = message("assistant", result.assistantText, result.answer, {
      ...(openRef.current ? { presentation: "reply" } : {}),
      ...(reviewTurnId ? { reviewTurnId, review: null } : {}),
      ...(projection?.formChangedDuringTurn ? { staleFormChange: true } : changedDraft ? { changedDraft: true } : {})
    });
    pendingReplyMessageRef.current = reply.presentation ? reply : null;
    scrollToBottomRef.current = true;
    const position = { revisionId: result.revisionId, sequence: result.sequence };
    setState((current) => {
      const currentSession = session || current.session;
      return {
        ...current,
        session: currentSession ? { ...currentSession, state: position } : null,
        messages: [...current.messages, reply],
        unavailable: false,
        credentialSuperseded: false,
        error: null,
        daily
      };
    });
    if (!wasAnalysisReady && changedDraft && manualEditRevisionAtReply === manualEditRevisionRef.current && !String(result.assistantText || "").includes("נתח עסקה")) readinessNoticeCandidateRef.current = { dealId, manualEditRevision: manualEditRevisionAtReply, replyText: String(result.assistantText || "") };
    startBlush();
    appendAnalysisReadyNotice();
  };

  const updateMessageReview = (reviewTurnId, changes) => setState((current) => ({ ...current,
    messages: current.messages.map((item) => item.reviewTurnId === reviewTurnId ? { ...item, ...changes } : item) }));

  // The rating shows at once. Saves of one answer run in order, so a thumbs-down cannot overwrite its later comment;
  // only the latest save updates the panel, and a failed one restores the previous rating and says so under the buttons.
  const saveAnswerReview = (reviewTurnId, review) => {
    const previous = messagesRef.current.find((item) => item.reviewTurnId === reviewTurnId)?.review || null;
    updateMessageReview(reviewTurnId, { review, reviewSaving: true, reviewFailed: false });
    const prior = reviewSavesRef.current.get(reviewTurnId) || Promise.resolve();
    const isLatest = () => reviewSavesRef.current.get(reviewTurnId) === saving;
    const saving = prior.then(() => aiAssistantService.saveFeedback({ schemaVersion: "AiAssistantFeedbackV1", dealId, turnId: reviewTurnId,
      rating: review ? review.rating : null, reasons: review ? review.reasons : [], comment: review ? review.comment : null }))
      .then((result) => { if (isLatest()) updateMessageReview(reviewTurnId, { review: result.review || null, reviewSaving: false }); },
        () => { if (isLatest()) updateMessageReview(reviewTurnId, { review: previous, reviewSaving: false, reviewFailed: true }); })
      .finally(() => { if (isLatest()) reviewSavesRef.current.delete(reviewTurnId); });
    reviewSavesRef.current.set(reviewTurnId, saving);
    return saving;
  };

  // A second click on the chosen thumb removes the rating; a new thumbs-down also asks what was wrong.
  const rateAnswer = (item, rating) => {
    if (item.review?.rating === rating) return saveAnswerReview(item.reviewTurnId, null);
    saveAnswerReview(item.reviewTurnId, { rating, reasons: [], comment: null });
    if (rating === "down") setReviewDialogTurnId(item.reviewTurnId);
  };

  const reviewDialogMessage = reviewDialogTurnId ? state.messages.find((item) => item.reviewTurnId === reviewDialogTurnId) || null : null;

  // A turn is one lawyer question: its turnId and request are fixed when it is asked, and every resend repeats that
  // request on the current session, so the server returns a saved answer instead of running the question again.
  const submitTurn = (text, appendUser = true, fromHint = false, composerText = text, retryTurn = null) => {
    if (!text || !state.session || state.isSending || submitLockRef.current || state.isOpening || state.unavailable || state.credentialSuperseded || limitReached) return;
    let turn = retryTurn;
    if (!turn) {
      const turnId = newTurnId();
      // Without a secure random source no turn can be identified: report it and keep the question in the composer.
      if (!turnId) {
        setState((current) => ({ ...current, error: { code: "TURN_ID_UNAVAILABLE", retryable: false } }));
        return;
      }
      const dealState = getDealState();
      turn = {
        request: { schemaVersion: "AiAssistantTurnV1", dealId, turnId, input: text, expectedRevisionId: state.session.state?.revisionId || null, expectedConversationSequence: state.session.state?.sequence || 0, dealState, ...(formProgress ? { formProgress } : {}) },
        turnContext: { dealState },
        composerText
      };
    }
    submitLockRef.current = true;
    const requestGeneration = requestGenerationRef.current;
    const submittedMessage = appendUser ? message("user", text, null, { presentation: "prompt", turnId: turn.request.turnId, ...(fromHint ? { fromHint: true } : {}) }) : null;
    const transcriptAfterSubmit = submittedMessage ? [...state.messages, submittedMessage] : state.messages;
    if (appendUser) {
      setInput("");
      if (showIntro && !introIsDocked) dockIntro(false);
      if (showIntro) setIntroHasPlayed(true);
    }
    scrollToBottomRef.current = true;
    setState((current) => ({ ...current, isSending: true, error: appendUser ? null : current.error, messages: transcriptAfterSubmit }));
    lastTurnRequestRef.current = turn;
    const sendTurnOn = (sessionIdRef) => aiAssistantService.sendTurn({ ...turn.request, sessionIdRef });
    sendTurnOn(state.session.sessionIdRef)
      .then((result) => ({ result, session: null }))
      .catch((error) => {
        if (!["AUTH_SESSION_EXPIRED", "SESSION_ENDED"].includes(errorDetails(error).code)) throw error;
        return aiAssistantService.openSession(dealId).then(({ history, ...session }) => {
          if (requestGeneration !== requestGenerationRef.current) return null;
          return sendTurnOn(session.sessionIdRef).then((result) => ({ result, session }));
        });
      })
      .then((turnResult) => {
        if (!turnResult || requestGeneration !== requestGenerationRef.current) return;
        return completeTurn(turnResult.result, turnResult.session, requestGeneration, turn.turnContext, turn.request.turnId).then(() => {
          if (requestGeneration === requestGenerationRef.current) lastTurnRequestRef.current = null;
        });
      })
      .catch(async (error) => {
        if (requestGeneration !== requestGenerationRef.current) return;
        const details = errorDetails(error);
        if (details.correlationRef) console.warn("AI assistant request failed", details.correlationRef);
        const responseBody = error?.response?.data?.error || error?.response?.data || error?.error || error;
        // Unknown outcomes stay in the transcript for Retry; permanent client rejections return the question to the composer.
        if (!["DAILY_LIMIT_REACHED", "STALE_REVISION", "INPUT_TOO_LONG"].includes(details.code)) {
          if (isPermanentTurnRejection(error)) {
            lastTurnRequestRef.current = null;
            setInput((current) => current.trim() ? current : turn.composerText);
            setState((current) => ({
              ...handleTurnFailure(current, error, true),
              unavailable: false,
              messages: current.messages.filter((item) => item.turnId !== turn.request.turnId),
              error: { ...details, retryable: false, message: "ההודעה נדחתה ולא נשלחה." }
            }));
          } else {
            setState((current) => ({ ...handleTurnFailure(current, error, true), error: { ...details, retryable: true } }));
          }
          return;
        }
        // The server checks a repeated turnId before these rejections, so the question was definitely not taken:
        // it leaves the transcript and returns to the composer only when the lawyer has not typed anything newer.
        lastTurnRequestRef.current = null;
        setInput((current) => current.trim() ? current : turn.composerText);
        if (details.code === "INPUT_TOO_LONG") {
          setState((current) => handleTurnFailure({ ...current, messages: current.messages.filter((item) => item !== submittedMessage) }, error, true));
          return;
        }
        // The conversation may hold newer turns: adopt its snapshot.
        const dailyLimited = details.code === "DAILY_LIMIT_REACHED";
        await recoverConversationAfterTurnError({
          requestGeneration,
          adoption: {
            removeMessage: submittedMessage,
            ...(dailyLimited ? { daily: { remaining: 0, limit: typeof responseBody.limit === "number" ? responseBody.limit : null, resetAt: typeof responseBody.resetAt === "string" ? responseBody.resetAt : null } } : {})
          },
          onRecovered: dailyLimited ? null : () => setState((current) => ({ ...current, error: { status: 409, retryable: false } }))
        });
      })
      .finally(() => {
        submitLockRef.current = false;
        if (requestGeneration === requestGenerationRef.current) setState((current) => ({ ...current, isSending: false }));
      });
  };

  const send = (event) => {
    event.preventDefault();
    submitTurn(input.trim(), true, false, input);
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    if (canSendTurn(state, input)) submitTurn(input.trim(), true, false, input);
  };

  const retryLastTurn = () => {
    const turn = lastTurnRequestRef.current;
    if (!turn || state.isSending) return;
    submitTurn(turn.request.input, false, false, undefined, turn);
  };

  const sendHint = (hint) => {
    if (input.trim() || !state.session) return;
    submitTurn(hint, true, true);
  };

  const selectHint = (hint) => {
    if (!hint.response) {
      sendHint(hint.text);
      return;
    }
    if (input.trim() || !state.session || state.isSending || state.isOpening) return;
    startBlush();
    scrollToBottomRef.current = true;
    setState((current) => ({
      ...current,
      messages: [...current.messages, message("user", hint.text, null, { fromHint: true, presentation: "prompt" }), ...hint.response.map((text) => message("assistant", text, null, { localOnly: true, presentation: "reply" }))]
    }));
  };

  const closePanel = () => {
    closedScrollTopRef.current = threadRef.current ? threadRef.current.scrollTop : null;
    closedMessageCountRef.current = messagesRef.current.length;
    cancelHistoryRequest();
    clearBlush();
    attemptedDealRef.current = null;
    onOpenChange(false);
    setMenuOpen(false);
    setIntroHasPlayed(true);
    pendingReplyMessageRef.current = null;
    setLauncherReturning(true);
    window.clearTimeout(launcherReturnTimeoutRef.current);
    launcherReturnTimeoutRef.current = window.setTimeout(() => setLauncherReturning(false), 375);
    setState((current) => ({ ...current, isLoadingHistory: false, messages: current.messages.map((item) => {
      if (!item.presentation) return item;
      const { presentation, ...messageWithoutPresentation } = item;
      return messageWithoutPresentation;
    }) }));
    window.requestAnimationFrame(() => launcherRef.current?.focus());
  };
  closePanelRef.current = closePanel;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (reviewDialogTurnId) setReviewDialogTurnId(null);
      else if (menuOpen) {
        setMenuOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
      } else closePanelRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, menuOpen, reviewDialogTurnId]);

  const notice = state.credentialSuperseded
    ? { kind: "error", text: "נכנסת למערכת בחלון אחר. כדי להמשיך כאן, יש להתחבר מחדש.", action: "התחברות מחדש", onAction: () => { window.location.href = "/sessions/login"; } }
    : state.unavailable
      ? { kind: "error", text: "העוזר אינו זמין לעסקה הזו.", action: "נסו שוב", onAction: () => { attemptedDealRef.current = null; openSession(true); } }
      : state.error
        ? state.error.code === "INPUT_TOO_LONG"
          ? { kind: "error", text: "ההודעה ארוכה מדי. יש לקצר אותה ל־4,000 תווים לכל היותר ולשלוח שוב." }
          : state.error.code === "TURN_ID_UNAVAILABLE"
            ? { kind: "error", text: "לא ניתן ליצור מזהה מאובטח לשליחה בדפדפן הזה." }
            : { kind: "error", text: state.error.message || "לא הצלחתי לענות. נסו לשלוח שוב.", ...(state.error.retryable === false ? {} : { action: "שלחו שוב", onAction: retryLastTurn }) }
        : dailyLimitReached
          ? { kind: "notice", text: "הגעתם למכסת ההודעות היומית. אפשר להמשיך מחר." }
          : null;
  const safeFullName = typeof fullName === "string" ? fullName.trim() : "";
  const greetingName = safeFullName && !GREETING_NAME_PLACEHOLDERS.has(safeFullName) ? safeFullName : null;
  const introContent = showIntro && !state.isOpening && !state.error && !state.unavailable && !state.credentialSuperseded
    ? <section className="ai-assistant-greeting" aria-label="היכרות עם ביבוט"><div ref={introMessageRef} className={`ai-assistant-intro-message${introIsDocked ? introTraveling ? " ai-assistant-intro-travel" : introFading ? " ai-assistant-intro-fade" : " ai-assistant-intro-static" : " ai-assistant-speech"}`} onAnimationEnd={(event) => { if (event.animationName === "ai-assistant-intro-travel") setIntroTraveling(false); if (event.animationName === "ai-assistant-intro-fade") setIntroFading(false); }}><strong>{greetingName ? `שלום ${greetingName}!` : "שלום!"}</strong><div className="ai-assistant-intro-guidance">אני ביבוט, העוזר האישי החדש שלך!</div><div className="ai-assistant-intro-guidance">אני כאן כדי לעזור לך בעבודה על העסקה — בצורה פשוטה ונוחה!</div></div></section>
    : null;

  const robotImages = (pose) => <><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-natural${pose === "natural" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotNatural} alt="" /><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-selections${pose === "selections" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotSelections} alt="" /><img className={`ai-assistant-avatar-pose ai-assistant-avatar-pose-blushing${pose === "blushing" ? " ai-assistant-avatar-pose-active" : ""}`} src={robotBlushing} alt="" /></>;
  if (!open) return <button ref={launcherRef} className={`ai-assistant-launcher${launcherReturning ? " ai-assistant-launcher-returning" : ""}`} type="button" aria-label="עוזר AI" onClick={() => onOpenChange(true)}>{robotImages("natural")}</button>;
  return <section ref={nodeRef} tabIndex={-1} className="ai-assistant-panel" aria-label="עוזר AI" dir="rtl">
      <header className="ai-assistant-header"><button className="ai-assistant-header-control ai-assistant-close" type="button" aria-label="סגירה" onClick={closePanel}><img src={backIcon} alt="" /></button><button ref={menuButtonRef} className="ai-assistant-header-control ai-assistant-menu-button" type="button" aria-label="אפשרויות שיחה" aria-expanded={menuOpen} aria-controls="ai-assistant-menu" onClick={() => setMenuOpen((isOpen) => !isOpen)}><img src={moreIcon} alt="" /></button><div id="ai-assistant-menu" className="ai-assistant-menu" hidden={!menuOpen}><button className="ai-assistant-menu-clear" type="button" aria-label="נקה צ'אט" disabled={state.isSending || state.isOpening} title="פותח שיחה חדשה וריקה ומשאיר את היסטוריית השיחה הקודמת" aria-describedby="ai-assistant-clear-chat-description" onClick={startNewConversation}><img src={menuTrashIcon} alt="" /><span>נקה צ'אט</span></button><span id="ai-assistant-clear-chat-description" className="ai-assistant-sr-only">פותח שיחה חדשה וריקה ומשאיר את היסטוריית השיחה הקודמת.</span></div></header>
      <div ref={bodyRef} className="ai-assistant-body">
      <div ref={threadRef} className="ai-assistant-thread" onScroll={(event) => { const thread = event.currentTarget; followLatestMessageRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24; if (thread.scrollTop <= 24) loadPreviousHistory(); }}>{state.isLoadingHistory ? <div className="ai-assistant-history-loading" role="status">טוענים הודעות קודמות…</div> : null}{state.isOpening ? <div className="ai-assistant-notice">פותחים שיחה…</div> : null}{state.messages.length || introIsDocked ? introContent : null}{state.messages.map((item, index) => item.type === "event" ? <div key={`event-${index}`} className="ai-assistant-event" role="status">{renderMixedDirectionText(item.text)}</div> : <React.Fragment key={item.historySeq || `${item.role}-${index}`}><div ref={item === pendingReplyMessageRef.current ? replyNodeRef : null} style={{ "--assistant-arrival-y": "0px" }} onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget || !item.presentation) return;
        setState((current) => ({ ...current, messages: current.messages.map((currentMessage) => {
          if (currentMessage !== item) return currentMessage;
          const { presentation, ...messageWithoutPresentation } = currentMessage;
          return messageWithoutPresentation;
        }) }));
      }} className={`ai-assistant-bubble ${item.role === "user" ? "ai-assistant-user" : "ai-assistant-bot"}${item.fromHint ? " ai-assistant-hint-message" : ""}${item.presentation ? ` ai-assistant-arrive-${item.presentation}` : ""}`}><span className="ai-assistant-who">{item.role === "user" ? "אתם" : "עוזר AI"}</span>{item.fromHint ? <img className="ai-assistant-prompt-mark" src={promptMarkIcon} alt="" /> : null}{item.role === "assistant" ? renderAssistantText(item.text) : renderMixedDirectionText(item.text)}{item.role === "assistant" && answerKindText(item.answerKind) ? <em className="ai-assistant-answer-kind">{answerKindText(item.answerKind)}</em> : null}{item.role === "assistant" && item.staleFormChange ? <em className="ai-assistant-stale-form" style={{ display: "block", marginTop: 8, color: "#5e6b84", fontSize: 13, fontStyle: "italic" }}>{STALE_FORM_NOTICE}</em> : null}{item.role === "assistant" && item.changedDraft ? <em className="ai-assistant-draft-change">{DRAFT_CHANGE_NOTICE}</em> : null}</div>{answerReviewEnabled && item.role === "assistant" && item.reviewTurnId ? <AnswerReviewButtons review={item.review} saving={item.reviewSaving} failed={item.reviewFailed} onRate={(rating) => rateAnswer(item, rating)} onEditComment={() => setReviewDialogTurnId(item.reviewTurnId)} /> : null}</React.Fragment>)}</div>
      <div ref={lowerStageRef} className="ai-assistant-lower-stage">
        <div className="ai-assistant-speech-slot">{!state.messages.length && !introIsDocked ? introContent : null}{suggestionsVisible ? <div className="ai-assistant-speech ai-assistant-suggestions-guidance">אפשר לכתוב טקסט חופשי, או לבחור באחת מהאפשרויות הבאות</div> : null}{state.isSending ? <div className="ai-assistant-typing" role="status" aria-label="העוזר כותב תשובה"><span className="ai-assistant-sr-only">העוזר כותב תשובה</span><span className="ai-assistant-typing-dots" aria-hidden="true"><span /><span /><span /></span></div> : null}</div>
        <div className="ai-assistant-character-stage"><div className="ai-assistant-avatar-wrap"><img className="ai-assistant-avatar-backdrop" src={pathBackdrop} alt="" /><div ref={avatarRef} className="ai-assistant-avatar">{robotImages(robotPose)}</div></div>{suggestionsVisible ? <div className="ai-assistant-hints ai-assistant-hints-docked">{ASSISTANT_HINTS.map((hint, index) => <button key={hint.text} type="button" style={{ "--assistant-hint-index": index }} onClick={() => selectHint(hint)}><img src={hint.icon} alt="" /><span>{hint.text}</span></button>)}</div> : null}</div>
      </div>
      </div>
      {notice ? <div className={`ai-assistant-bottom-notice ai-assistant-bottom-notice-${notice.kind}`} role={notice.kind === "error" ? "alert" : "status"}><span>{notice.text}</span>{notice.action ? <button type="button" onClick={notice.onAction} disabled={state.isSending || state.isOpening}>{notice.action}</button> : null}</div> : null}
      {answerReviewEnabled && reviewDialogMessage ? <AnswerReviewDialog key={reviewDialogTurnId} review={reviewDialogMessage.review} onCancel={() => setReviewDialogTurnId(null)} onSave={(review) => { setReviewDialogTurnId(null); saveAnswerReview(reviewDialogTurnId, review); }} /> : null}
      <form ref={composerRef} className="ai-assistant-composer" onSubmit={send}>{typeof state.daily.remaining === "number" && state.daily.remaining >= 1 && state.daily.remaining <= 3 ? <div className="ai-assistant-daily-remaining" role="status">הודעות שנותרו להיום: {state.daily.remaining}</div> : null}<div className="ai-assistant-entry"><textarea ref={inputRef} aria-label="הודעה" placeholder="הקלד הודעה..." value={input} disabled={state.isSending || state.isOpening || limitReached} onChange={(event) => setInput(event.target.value)} onKeyDown={handleInputKeyDown} /><textarea ref={inputMeasureRef} className="ai-assistant-entry-measure" aria-hidden="true" tabIndex={-1} rows={1} value={input} readOnly /><button type="submit" aria-label="שליחה" disabled={!canSendTurn(state, input) || limitReached}><img src={sendIcon} alt="" /></button></div></form>
    </section>;
}
